// "This JS context dispatched an account deletion that has not been contradicted."
//
// Deleting an account tears down `users/{uid}` while the app is still mounted
// and still listening to it. Every consumer of that node sees the null (or
// half-removed) snapshot, and DashboardView reads a profile with no `name` as
// "brand new user" and mounts the OnboardingWizard behind the confirmation
// sheet — a wizard whose writes the tombstone rule denies and which has no
// dismiss path. The same snapshots also drive the region/timezone startup
// writes, which re-create the node the backend just deleted.
//
// This module is the seam that tells those two situations apart. It is
// deliberately dependency-free: DashboardView imports it and must not, through
// it, acquire firebase/auth, authUtils (router, push notifications, admin
// store) or cloudFunctions, none of which it imports today.
//
// The state is a PAIR, not a boolean. The reset rule needs the uid:
//   - set when the delete request is dispatched;
//   - cleared on every failure path (the account is still live, normal
//     behavior has to come back immediately);
//   - after success it must survive the sign-out deletion performs, because
//     uid -> null happens INSIDE the window this flag exists to cover. Only a
//     different, non-null signed-in uid means a new session that deserves
//     normal behavior.
let deletionUid = null;
let deletionInFlight = false;

// Each dispatch gets a token so a clear can say WHICH request it speaks for.
// A view can be left and re-entered while a slow deletion is still running,
// which produces a second composable instance and a second dispatch; the first
// request failing afterwards must not lift the second one's protection.
let dispatchToken = 0;

/**
 * Record that a deletion request has just been sent for `uid`.
 * @param {string|null|undefined} uid the account being deleted, if known.
 * @returns {number} a token to hand back to clearAccountDeletion.
 */
export function markAccountDeletionDispatched(uid) {
  deletionUid = uid || null;
  deletionInFlight = true;
  dispatchToken += 1;
  return dispatchToken;
}

/**
 * Deletion did not happen (or is no longer relevant): restore normal behavior.
 * @param {number} [token] the dispatch this clear belongs to. A stale token is
 *   a superseded request finishing late and is ignored. Omitting the token
 *   means "clear regardless", which is what the reset rule needs when a
 *   genuinely different account signs in.
 */
export function clearAccountDeletion(token) {
  if (token !== undefined && token !== dispatchToken) return;
  deletionUid = null;
  deletionInFlight = false;
}

/** @returns {boolean} true while a dispatched deletion has not been contradicted. */
export function isAccountDeletionInFlight() {
  return deletionInFlight;
}

/**
 * The reset rule's observer input: auth just reported `uid` as the signed-in
 * account. Called from `authStore.setUserId`, the one choke point every
 * auth-state path in the app already goes through — no view observes uid
 * changes itself (DashboardView's listener binds once per mount).
 *
 * A falsy uid is the sign-out that a successful deletion performs, so it must
 * NOT clear. The same uid being re-reported (a token refresh landing before
 * sign-out completes) must not clear either.
 *
 * @param {string|null|undefined} uid
 */
export function noteSignedInUid(uid) {
  if (!deletionInFlight) return;
  if (!uid) return;
  if (uid === deletionUid) return;
  clearAccountDeletion();
}

// Test-only: module state outlives a component and a test case. Several suites
// instantiate useAccountDeletion many times in one module, so without an
// explicit reset a single successful-deletion case would poison every later one.
export function _resetAccountDeletionStateForTests() {
  clearAccountDeletion();
  dispatchToken = 0;
}
